import { useMemo, useState } from "react";
import { BookOpen, MapPin, Search } from "lucide-react";
import { EMPLOYEE_HELP_TOPICS } from "./employeeModuleHelpContent";
import HelpUiSnippet from "./HelpUiSnippet";
import { DsIcon, DsInput, DsPageHeader } from "../../../design-system";

export default function EmployeeModuleHelp({ darkMode = false }) {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(EMPLOYEE_HELP_TOPICS[0]?.id || "");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return EMPLOYEE_HELP_TOPICS;
    return EMPLOYEE_HELP_TOPICS.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.summary.toLowerCase().includes(q) ||
        t.location.toLowerCase().includes(q) ||
        (t.features || []).some((f) => f.toLowerCase().includes(q)) ||
        (t.processFlows || []).some(
          (flow) =>
            flow.title.toLowerCase().includes(q) ||
            (flow.steps || []).some((step) => step.toLowerCase().includes(q))
        )
    );
  }, [query]);

  const active =
    filtered.find((t) => t.id === activeId) || filtered[0] || EMPLOYEE_HELP_TOPICS[0];

  return (
    <div className="ds-page-panel flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="ds-page-panel-head">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <DsIcon
              icon={BookOpen}
              size="md"
              className="mt-0.5 text-[hsl(var(--ds-primary))]"
            />
            <DsPageHeader
              title="Employee Module Help"
              description="Module reference — screens, features, and configuration options."
              className="mb-0"
            />
          </div>
          <div className="relative w-full max-w-xs">
            <DsIcon
              icon={Search}
              size="sm"
              className="pointer-events-none absolute left-2.5 top-1/2 z-[1] -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
            />
            <DsInput
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search features, screens…"
              className="pl-8"
            />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="ds-masters-rail w-56 shrink-0 !border-b-0">
          {filtered.map((topic) => (
            <button
              key={topic.id}
              type="button"
              onClick={() => setActiveId(topic.id)}
              className={
                active?.id === topic.id
                  ? "ds-page-nav-item ds-page-nav-item-active"
                  : "ds-page-nav-item"
              }
            >
              {topic.title}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="ds-caption px-3 py-4 text-[hsl(var(--ds-muted-fg))]">
              No topics match your search.
            </p>
          )}
        </aside>

        <div className="ds-masters-pane min-h-0 flex-1 overflow-y-auto">
          {active && (
            <div className="mx-auto max-w-3xl space-y-4">
              <div>
                <h3 className="ds-h3 text-[hsl(var(--ds-fg))]">{active.title}</h3>
                <p className="ds-body mt-1 text-[hsl(var(--ds-muted-fg))]">{active.summary}</p>
                <p className="ds-caption mt-2 inline-flex items-center gap-1 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-2 py-1 text-[hsl(var(--ds-muted-fg))]">
                  <DsIcon icon={MapPin} size="sm" />
                  {active.location}
                </p>
              </div>

              <div>
                <h4 className="ds-h4 mb-2 text-[hsl(var(--ds-fg))]">UI preview</h4>
                <HelpUiSnippet variant={active.snippet} darkMode={darkMode} />
              </div>

              <div>
                <h4 className="ds-h4 mb-2 text-[hsl(var(--ds-fg))]">Features & options</h4>
                <ul className="list-disc space-y-2 pl-5 ds-body text-[hsl(var(--ds-fg))]">
                  {(active.features || []).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>

              {(active.processFlows || []).length > 0 && (
                <div>
                  <h4 className="ds-h4 mb-2 text-[hsl(var(--ds-fg))]">Process flows</h4>
                  <div className="space-y-4">
                    {(active.processFlows || []).map((flow) => (
                      <div
                        key={flow.title}
                        className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] p-3"
                      >
                        <h5 className="ds-body font-semibold text-[hsl(var(--ds-fg))]">
                          {flow.title}
                        </h5>
                        <ol className="mt-2 list-decimal space-y-2 pl-5 ds-body text-[hsl(var(--ds-fg))]">
                          {(flow.steps || []).map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.12)] p-3 ds-caption text-[hsl(var(--ds-warning-fg))]">
                Tip: Use the left nav in Employee to open the screen described above, then follow the
                feature list for configuration options.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
